import React from "react";
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Avatar,
  Chip,
} from "@mui/material";
import {
  DirectionsCar,
  LocationOn,
  People,
  Engineering,
  CompareArrows,
  TrendingUp,
  Dashboard,
  FileDownload,
  GpsFixed,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import NavigationHeader from "../../components/NavigationHeader";
import { useResponsiveBreakpoints } from "../../utils/responsiveUtils";

export const fuelReportTypes = [
  {
    id: 1,
    label: "Vehicle Consumption",
    description:
      "Detailed fuel consumption and mileage data for each vehicle in your fleet",
    icon: <DirectionsCar />,
    type: "vehicle-consumption",
    color: "#2196F3",
    shortLabel: "Vehicle Consumption",
  },
  {
    id: 2,
    label: "Regional Analysis",
    description:
      "Fuel consumption patterns across different regions, zones, and districts",
    icon: <LocationOn />,
    type: "regional-analysis",
    color: "#4CAF50",
    shortLabel: "Regional Analysis",
  },
  // {
  //   id: 3,
  //   label: "Staff Performance",
  //   description:
  //     "Fuel consumption performance by staff members and their assigned vehicles",
  //   icon: <People />,
  //   type: "staff-performance",
  //   color: "#FF9800",
  //   shortLabel: "Staff Performance",
  // },
  // {
  //   id: 4,
  //   label: "Model Efficiency",
  //   description:
  //     "Fuel efficiency patterns across different vehicle models and age categories",
  //   icon: <Engineering />,
  //   type: "model-efficiency",
  //   color: "#9C27B0",
  //   shortLabel: "Model Efficiency",
  // },
  // {
  //   id: 5,
  //   label: "Source Comparison",
  //   description: "Compare fuel consumption data across different data sources",
  //   icon: <CompareArrows />,
  //   type: "source-comparison",
  //   color: "#607D8B",
  //   shortLabel: "Source Comparison",
  // },
  // {
  //   id: 6,
  //   label: "Month-on-Month",
  //   description:
  //     "Month-by-month fuel consumption analysis with percentage changes and trends",
  //   icon: <TrendingUp />,
  //   type: "month-on-month",
  //   color: "#F44336",
  //   shortLabel: "Month-on-Month",
  // },
  // {
  //   id: 7,
  //   label: "Dashboard Analytics",
  //   description:
  //     "Comprehensive overview combining data from all reports for dashboard display",
  //   icon: <Dashboard />,
  //   type: "dashboard",
  //   color: "#3F51B5",
  //   shortLabel: "Dashboard",
  // },
  // {
  //   id: 8,
  //   label: "Export Reports",
  //   description: "Export report data in CSV format for external analysis",
  //   icon: <FileDownload />,
  //   type: "export",
  //   color: "#795548",
  //   shortLabel: "Export",
  // },
  // {
  //   id: 9,
  //   label: "Vehicle GPS & Fuel Report",
  //   description:
  //     "Comprehensive report combining GPS tracking data with fuel consumption metrics for fleet insights",
  //   icon: <GpsFixed />,
  //   type: "vehicle-gps-fuel-report",
  //   color: "#00BCD4",
  //   shortLabel: "GPS & Fuel Report",
  // },
];

const FuelReportsPage: React.FC = () => {
  const navigate = useNavigate();

  // Use responsive breakpoints for layout optimization
  const {
    isMobile,
    isTablet,
    isSmallDesktop,
    getGridColumns,
    getSpacing,
    screenCategory,
  } = useResponsiveBreakpoints();

  // Calculate responsive grid columns
  const getResponsiveColumns = () => {
    const totalItems = fuelReportTypes.length;

    if (isMobile) return 12; // Full width on mobile
    if (isTablet) return 6; // 2 columns on tablet
    if (isSmallDesktop && totalItems > 4) return 3; // 4 columns on small desktop
    return Math.max(3, Math.floor(12 / Math.min(6, totalItems))); // Dynamic sizing for larger screens
  };

  const handleCardClick = (reportType: string) => {
    navigate(`/fuel-reports/${reportType}`);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <NavigationHeader title="Fuel Reports" backNavigationEnabled={false} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Comprehensive fuel analytics and reporting capabilities for your
            fleet
          </Typography>
          <Chip
            label="Minimum 28 days required for meaningful analysis"
            size="small"
            color="info"
            variant="outlined"
          />
        </Box>

        <Grid container spacing={isMobile ? 2 : isTablet ? 2.5 : 3}>
          {fuelReportTypes.map((reportType) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={getResponsiveColumns()}
              key={reportType.id}
            >
              <Card
                sx={{
                  height: "100%",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: 4,
                  },
                  cursor: "pointer",
                  minHeight: isMobile ? "140px" : isTablet ? "160px" : "180px",
                }}
              >
                <CardActionArea
                  onClick={() => handleCardClick(reportType.type)}
                  sx={{
                    height: "100%",
                    p: isMobile ? 1 : isTablet ? 1.5 : 2,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <CardContent
                    sx={{
                      textAlign: "center",
                      p: isMobile ? 1 : 1.5,
                      "&:last-child": {
                        paddingBottom: isMobile ? 1 : 1.5,
                      },
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: reportType.color,
                        width: isMobile ? 35 : isTablet ? 40 : 40,
                        height: isMobile ? 35 : isTablet ? 40 : 40,
                        mx: "auto",
                        mb: isMobile ? 1 : 1.5,
                        fontSize: isMobile ? "1rem" : "1.25rem",
                      }}
                    >
                      {reportType.icon}
                    </Avatar>

                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "bold",
                        fontSize: isMobile
                          ? "0.7rem"
                          : isTablet
                          ? "0.75rem"
                          : "0.8rem",
                        lineHeight: 1.2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        mb: 1,
                      }}
                    >
                      {reportType.label}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontSize: isMobile ? "0.65rem" : "0.75rem",
                        lineHeight: 1.3,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: isMobile ? 2 : isTablet ? 2 : 3,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {reportType.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  );
};

export default FuelReportsPage;
